/**
 * Training Module Editor — create a module, edit its draft, publish versions,
 * archive / restore. Training administration only (Central training:write,
 * the same capability the server requires; the route is guarded too).
 *
 * Editing never changes a published version: Save writes the module's draft,
 * Publish freezes the draft into the next immutable version (learners already
 * assigned keep the version they were assigned). Every reference — scenarios,
 * scenario tags, rubric, Learn lessons, domain — is picked from the canonical
 * lists the server returns (GET /training/modules/authoring-options), and the
 * server validates them again.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useCreateTrainingModuleMutation,
  useGetTrainingAuthoringOptionsQuery,
  useGetTrainingModuleQuery,
  usePublishTrainingModuleMutation,
  useSaveTrainingModuleDraftMutation,
  useSetTrainingModuleArchivedMutation,
  type TrainingAuthoringOptions,
  type TrainingModuleDefinition,
  type TrainingModuleInput,
  type EmphasisProfile,
} from '../store/api/trainingApi';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { ModuleStatusBadge, TRAINING_ROUTES, TrainingErrorNotice, VersionLabel } from '../components/training/trainingUi';

interface FormState {
  name: string;
  description: string;
  domainKey: string;
  scenarioKey: string;
  emphasisProfile: EmphasisProfile;
  minOverallScore: string;
  minClarityScore: string;
  scenarioIds: string[];
  scenarioTags: string[];
  rubric: string; // "id@version" or ''
  lessons: string[]; // "moduleSlug/lessonId"
}

const EMPTY: FormState = {
  name: '',
  description: '',
  domainKey: '',
  scenarioKey: '',
  emphasisProfile: 'skill',
  minOverallScore: '70',
  minClarityScore: '',
  scenarioIds: [],
  scenarioTags: [],
  rubric: '',
  lessons: [],
};

export function formFromDefinition(d: TrainingModuleDefinition): FormState {
  return {
    name: d.name,
    description: d.description ?? '',
    domainKey: d.domainKey,
    scenarioKey: d.scenarioKey,
    emphasisProfile: d.emphasisProfile,
    minOverallScore: String(d.criteria.minOverallScore),
    minClarityScore: d.criteria.minClarityScore === null ? '' : String(d.criteria.minClarityScore),
    scenarioIds: [...d.content.scenarioIds],
    scenarioTags: [...d.content.scenarioTags],
    rubric: d.content.rubric ? `${d.content.rubric.id}@${d.content.rubric.version}` : '',
    lessons: d.content.lessons.map((l) => `${l.moduleSlug}/${l.lessonId}`),
  };
}

export function inputFromForm(f: FormState): TrainingModuleInput {
  const at = f.rubric.lastIndexOf('@');
  const rubric = f.rubric && at > 0 ? { id: f.rubric.slice(0, at), version: f.rubric.slice(at + 1) } : null;
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    domainKey: f.domainKey,
    scenarioKey: f.scenarioKey,
    emphasisProfile: f.emphasisProfile,
    minOverallScore: Number(f.minOverallScore),
    minClarityScore: f.minClarityScore.trim() === '' ? null : Number(f.minClarityScore),
    content: {
      scenarioIds: f.scenarioIds,
      scenarioTags: f.scenarioTags,
      rubric,
      lessons: f.lessons.map((key) => {
        const i = key.indexOf('/');
        return { moduleSlug: key.slice(0, i), lessonId: key.slice(i + 1) };
      }),
    },
  };
}

const toggle = (xs: string[], x: string) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);

export function TrainingModuleEditorPage() {
  const { moduleId } = useParams<{ moduleId?: string }>();
  const isNew = !moduleId;
  const navigate = useNavigate();
  const { hasCapability } = useCentralConsumer();
  const canManage = hasCapability('training', 'write');

  const options = useGetTrainingAuthoringOptionsQuery(undefined, { skip: !canManage });
  const detail = useGetTrainingModuleQuery(moduleId ?? '', { skip: isNew || !canManage });
  const [create, createState] = useCreateTrainingModuleMutation();
  const [save, saveState] = useSaveTrainingModuleDraftMutation();
  const [publish, publishState] = usePublishTrainingModuleMutation();
  const [setArchived, archiveState] = useSetTrainingModuleArchivedMutation();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Start from the draft when there is one, else the latest published version.
  const module = detail.data;
  useEffect(() => {
    if (!module || loadedFor === module.id) return;
    const d = module.draft?.definition ?? module.definition;
    if (d) setForm(formFromDefinition(d));
    setLoadedFor(module.id);
  }, [module, loadedFor]);

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" data-testid="training-editor-denied">
        <p className="text-gray-700">Managing training needs the training administration capability in this workspace.</p>
        <Link to={TRAINING_ROUTES.dashboard} className="text-primary-600 hover:underline">
          Back to training
        </Link>
      </div>
    );
  }

  if (options.isLoading || (!isNew && detail.isLoading)) {
    return <div className="p-6 text-gray-500">Loading…</div>;
  }
  if (options.isError || (!isNew && detail.isError)) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <LoadErrorNotice
          error={options.error ?? detail.error}
          onRetry={() => {
            void options.refetch();
            if (!isNew) void detail.refetch();
          }}
          failedMessage="This training module could not be loaded."
          notFound={<p data-testid="training-module-not-found">Training module not found.</p>}
          testIdPrefix="training-editor"
        />
      </div>
    );
  }
  const opts = options.data as TrainingAuthoringOptions;
  if (!opts.authoringActive) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" data-testid="training-authoring-off">
        <p className="text-gray-700">Creating and editing training modules is not switched on for this deployment yet.</p>
        <Link to={TRAINING_ROUTES.dashboard} className="text-primary-600 hover:underline">
          Back to training
        </Link>
      </div>
    );
  }

  const domain = opts.domains.find((d) => d.key === form.domainKey);
  // Distinct scenario tags among the scenarios the author can see.
  const tags = [...new Set(opts.scenarios.flatMap((s) => s.tags))].sort();
  const busy = createState.isLoading || saveState.isLoading || publishState.isLoading || archiveState.isLoading;
  const lastError = createState.error ?? saveState.error ?? publishState.error ?? archiveState.error;
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setNotice(null);
    const input = inputFromForm(form);
    if (isNew) {
      const created = await create(input).unwrap().catch(() => null);
      if (created) navigate(TRAINING_ROUTES.editModule(created.id), { replace: true });
    } else {
      const saved = await save({ moduleId: moduleId!, input }).unwrap().catch(() => null);
      if (saved) setNotice('Draft saved. Publish it to make it the version new assignments get.');
    }
  };

  const onPublish = async () => {
    setNotice(null);
    const done = await publish(moduleId!).unwrap().catch(() => null);
    if (done?.latestVersion) setNotice(`Published version ${done.latestVersion.version}.`);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="training-module-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to={TRAINING_ROUTES.dashboard} className="text-sm text-primary-600 hover:underline">
            ← Training
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'New training module' : module?.name}</h1>
          {module && (
            <div className="mt-1 flex items-center gap-2">
              <ModuleStatusBadge module={module} />
              {module.latestVersion && <VersionLabel version={module.latestVersion.version} contentHash={module.latestVersion.contentHash} />}
            </div>
          )}
        </div>
        {module && (
          <div className="flex gap-2">
            {module.isActive && module.status !== 'draft' && (
              <Link to={TRAINING_ROUTES.assignModule(module.id)} className="btn btn-secondary">
                Assign
              </Link>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => void setArchived({ moduleId: module.id, archived: module.isActive })}
            >
              {module.isActive ? 'Archive' : 'Restore'}
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-gray-600">
        Saving changes the draft only. Learners already assigned keep the exact version they were assigned; publishing makes
        the draft the version that new assignments get.
      </p>

      <TrainingErrorNotice error={lastError} />
      {notice && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status">
          {notice}
        </div>
      )}

      {module && !module.isActive ? (
        <p className="text-sm text-gray-600" data-testid="training-module-archived">
          This module is archived: it is hidden from learners, cannot be assigned or edited, and does not count toward your
          plan limit on active training modules. Restore it to continue (restoring needs room under that limit).
        </p>
      ) : (
        <form
          className="card p-6 space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
        >
          <Field label="Name">
            <input className="form-input w-full" value={form.name} maxLength={255} required onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea className="form-textarea w-full" value={form.description} maxLength={2000} onChange={(e) => set('description', e.target.value)} />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Domain">
              <select
                className="form-select w-full"
                value={form.domainKey}
                required
                onChange={(e) => setForm((f) => ({ ...f, domainKey: e.target.value, scenarioKey: '' }))}
              >
                <option value="">Choose a domain</option>
                {opts.domains.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Scenario key">
              <select className="form-select w-full" value={form.scenarioKey} required onChange={(e) => set('scenarioKey', e.target.value)}>
                <option value="">Choose a scenario key</option>
                {(domain?.scenarioKeys ?? []).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Minimum overall score (0–100)">
              <input
                type="number"
                min={0}
                max={100}
                className="form-input w-full"
                value={form.minOverallScore}
                required
                onChange={(e) => set('minOverallScore', e.target.value)}
              />
            </Field>
            <Field label="Minimum clarity score (optional)">
              <input
                type="number"
                min={0}
                max={100}
                className="form-input w-full"
                value={form.minClarityScore}
                onChange={(e) => set('minClarityScore', e.target.value)}
              />
            </Field>
            <Field label="Emphasis">
              <select className="form-select w-full" value={form.emphasisProfile} onChange={(e) => set('emphasisProfile', e.target.value as EmphasisProfile)}>
                <option value="skill">Skill</option>
                <option value="sale">Sale</option>
                <option value="knowledge">Knowledge</option>
                <option value="custom">Custom</option>
              </select>
            </Field>
            <Field label="Rubric">
              <select className="form-select w-full" value={form.rubric} onChange={(e) => set('rubric', e.target.value)}>
                <option value="">No rubric</option>
                {opts.rubrics.map((r) => (
                  <option key={`${r.id}@${r.version}`} value={`${r.id}@${r.version}`}>
                    {r.title} ({r.id}@{r.version})
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Fieldset legend="Practice scenarios">
            {opts.scenarios.length === 0 ? (
              <p className="text-sm text-gray-500">No scenarios are available to you.</p>
            ) : (
              opts.scenarios.map((s) => (
                <Check key={s.id} label={s.title} checked={form.scenarioIds.includes(s.id)} onChange={() => set('scenarioIds', toggle(form.scenarioIds, s.id))} />
              ))
            )}
          </Fieldset>

          {tags.length > 0 && (
            <Fieldset legend="Scenario tags">
              {tags.map((t) => (
                <Check key={t} label={t} checked={form.scenarioTags.includes(t)} onChange={() => set('scenarioTags', toggle(form.scenarioTags, t))} />
              ))}
            </Fieldset>
          )}

          <Fieldset legend="Learn lessons">
            {opts.lessons.map((m) => (
              <div key={m.moduleSlug} className="mb-2">
                <div className="text-sm font-medium text-gray-700">{m.moduleTitle}</div>
                {m.lessons.map((l) => {
                  const key = `${m.moduleSlug}/${l.id}`;
                  return <Check key={key} label={l.title} checked={form.lessons.includes(key)} onChange={() => set('lessons', toggle(form.lessons, key))} />;
                })}
              </div>
            ))}
          </Fieldset>

          <div className="flex flex-wrap gap-3">
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="training-save-draft">
              {isNew ? 'Create draft' : 'Save draft'}
            </button>
            {!isNew && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || !module?.hasDraft}
                onClick={() => void onPublish()}
                data-testid="training-publish"
                title={module?.hasDraft ? undefined : 'Save a draft first'}
              >
                Publish new version
              </button>
            )}
          </div>
        </form>
      )}

      {module && module.versions.length > 0 && (
        <div className="card p-6" data-testid="training-versions">
          <h2 className="font-semibold mb-2">Published versions</h2>
          <ul className="space-y-1 text-sm text-gray-600">
            {module.versions.map((v) => (
              <li key={v.id}>
                <VersionLabel version={v.version} contentHash={v.contentHash} /> · {new Date(v.publishedAt).toLocaleString()}
                {v.origin === 'legacy_baseline' && ' · baseline of the module as it was before versioning'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-700 mb-1">{legend}</legend>
      <div className="max-h-48 overflow-y-auto space-y-1">{children}</div>
    </fieldset>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
